import { useEffect } from 'react'
import { Link, useRouter } from '@tanstack/react-router'
import { Logo } from '@/components/ui/logo'
import { Button } from '@/components/ui/button'
import { BackgroundPattern } from '@/components/ui/background-pattern'
import { isChunkLoadError } from './root-error.helpers'

const RELOADED_KEY = 'root-error:reloaded-for'

export function RootErrorPage({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter()

  // Stale chunk after a deploy (see root-error.helpers.ts): a full reload gets
  // the fresh HTML + matching chunks. Once per URL, so a persistent failure
  // still lands on this page instead of a reload loop.
  useEffect(() => {
    if (!isChunkLoadError(error)) return
    try {
      if (sessionStorage.getItem(RELOADED_KEY) === location.href) return
      sessionStorage.setItem(RELOADED_KEY, location.href)
    } catch {
      return
    }
    location.reload()
  }, [error])

  return (
    <div className="relative flex min-h-screen flex-col">
      {/* This boundary replaces the root component, so the route's <HeadContent/>
          is gone: the document has no title and nothing telling crawlers to
          skip it. Google renders JS and indexed this page as a sitelink once.
          React 19 hoists these into <head>. */}
      <title>Une erreur est survenue | CountAct</title>
      <meta name="robots" content="noindex" />
      <BackgroundPattern />

      <header className="p-6">
        <Link to="/$locale" params={{ locale: 'fr' } as never} aria-label="CountAct">
          <Logo size="md" alt="CountAct" />
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="mx-auto max-w-xl text-center">
          <p className="font-display text-7xl font-semibold text-primary sm:text-8xl">
            Oups
          </p>
          <h1 className="mt-4 text-2xl font-semibold sm:text-3xl">
            Une erreur est survenue
          </h1>
          <p className="text-muted-foreground mt-3">
            Quelque chose s'est mal passé. Vous pouvez réessayer ou retourner à l'accueil.
          </p>
          {import.meta.env.DEV && error?.message && (
            <pre className="mt-4 rounded bg-muted p-3 text-left text-xs text-muted-foreground overflow-auto">
              {error.message}
            </pre>
          )}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => {
                reset()
                router.invalidate()
              }}
            >
              Réessayer
            </Button>
            <Button variant="outline" asChild>
              <Link to="/$locale" params={{ locale: 'fr' } as never}>
                Retour à l'accueil
              </Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  )
}
